import { AlertTriangle, Briefcase, MapPin, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useDateTimePreferences } from '../../hooks/useDateTimePreferences';
import { formatDistance, formatDuration, formatTimeLocal } from '../../utils/etaFormatters';
import {
  buildRiderTripSummaryModel,
  getRiderBookingLabel
} from '../../utils/riderTripSummary';

const getPhaseKey = (phase) => `riderTrip.phase.${phase || 'unknown'}`;

const formatLifecycleTime = (value, { formatTime, formatDateTime, t, entityTimeZone }) => (
  formatTimeLocal(value, formatTime, formatDateTime, t, entityTimeZone)
);

const formatMetricValue = (metric, { distanceUnit, formatTime, formatDateTime, t, entityTimeZone }) => {
  if (!metric) {
    return '';
  }

  if (metric.type === 'durationMinutes') {
    return formatDuration(metric.value, t);
  }
  if (metric.type === 'distanceMeters') {
    return formatDistance(metric.value, distanceUnit, t);
  }
  if (metric.type === 'time') {
    return formatLifecycleTime(metric.value, { formatTime, formatDateTime, t, entityTimeZone });
  }
  if (metric.type === 'status') {
    return t(metric.value);
  }
  if (metric.type === 'translationKey') {
    return t(metric.value);
  }
  return String(metric.value);
};

const formatMetricCompanionValue = (metric, { formatTime, formatDateTime, t, entityTimeZone }) => {
  if (!metric?.companionValue) {
    return null;
  }

  if (metric.companionType === 'time') {
    return formatLifecycleTime(metric.companionValue, { formatTime, formatDateTime, t, entityTimeZone });
  }

  if (metric.companionType === 'translationKey') {
    return t(metric.companionValue);
  }

  return String(metric.companionValue);
};

// Rider-facing connection wording. The technical WebSocket labels stay on the
// driver/admin tile; riders only need to know the information may be out of
// date. access_denied previously surfaced on the bottom tile, which is now
// driver/admin-only, so the card is the rider's only authorization notice.
const connectionNoticeKeys = {
  reconnecting: 'riderTrip.connection.reconnecting',
  stale_reconnecting: 'riderTrip.connection.reconnecting',
  disconnected: 'riderTrip.connection.unavailable',
  error: 'riderTrip.connection.unavailable',
  access_denied: 'accessDenied'
};

// One shared amber strip shape for every rider notice on the card, matching the
// existing amber alert styling used elsewhere in the trip view.
const NoticeStrip = ({ message, cn }) => (
  <div
    role="status"
    className={cn(
      'flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900',
      'dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100'
    )}
  >
    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
    <span>{message}</span>
  </div>
);

const RiderTripSummary = ({
  tripData,
  displayData,
  relevantStops = [],
  waypointStates = {},
  entitySettings = {},
  riderBookings = [],
  selectedBooking,
  onSelectBooking,
  entityTimeZone = null,
  connectionStatus = null,
  isStalePresentation = false,
  isAwaitingLiveEtaResume = false
}) => {
  const { t } = useTranslation();
  const { background, text, border, cn } = useTheme();
  const { distanceUnit, formatTime, formatDateTime } = useDateTimePreferences();
  const summaryModel = buildRiderTripSummaryModel({
    selectedBooking,
    relevantStops,
    displayData,
    tripStatus: tripData?.status,
    tripData,
    waypointStates,
    boardingTrackingEnabled: Boolean(entitySettings?.boarding_tracking_enabled),
    isStalePresentation
  });

  if (!selectedBooking || !summaryModel) {
    return null;
  }

  const pickupName = selectedBooking.pickup_stop_name || summaryModel.pickupRow?.waypoint_name || t('riderTrip.pickup');
  const dropoffName = selectedBooking.dropoff_stop_name || summaryModel.dropoffRow?.waypoint_name || t('riderTrip.dropoff');
  const formatTranslationValues = (values = {}) => ({
    ...values,
    time: values.time && typeof formatTime === 'function'
      ? formatLifecycleTime(values.time, { formatTime, formatDateTime, t, entityTimeZone })
      : values.time
  });

  // A completed trip, or a rider already dropped off, shows only settled
  // history: the arrival time and ride distance are final and no live update
  // is expected, so the socket closing is normal rather than a loss of trust.
  // Warning that the information "may be out of date" there would claim a
  // staleness that cannot exist. This applies only to transport-health
  // statuses: access_denied reports an authorization failure, which stays
  // true regardless of lifecycle, and this card is the rider's only notice
  // for it.
  const tripStatus = String(tripData?.status || '').trim().toLowerCase();
  const showsSettledHistory = tripStatus === 'completed'
    || summaryModel.phase === 'arrivedDropoff';
  const isTransportHealthStatus = connectionStatus !== 'access_denied';

  // Precedence: while the socket is unhealthy the browser cannot observe the
  // location feed at all, so the connection notice replaces the GPS-stale one.
  // A delay notice is independent evidence and may coexist with either.
  const connectionNoticeKey = showsSettledHistory && isTransportHealthStatus
    ? null
    : (connectionNoticeKeys[connectionStatus] || null);
  const staleNoticeKey = !connectionNoticeKey && isStalePresentation
    ? (isAwaitingLiveEtaResume ? 'riderTrip.stale.updatingEta' : 'riderTrip.stale.notice')
    : null;
  const delayNotice = summaryModel.delayNotice;

  return (
    <section
      aria-label={t('riderTrip.summary')}
      className={cn(
        'mb-4 rounded-lg border p-4',
        background.secondary,
        border?.default
      )}
    >
      {riderBookings.length > 1 && (
        <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
          {riderBookings.map((booking) => {
            const isSelected = booking.booking_id === selectedBooking.booking_id;
            return (
              <button
                key={booking.booking_id}
                type="button"
                onClick={() => onSelectBooking?.(booking.booking_id)}
                aria-pressed={isSelected}
                className={cn(
                  'min-w-fit rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                  isSelected
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'
                )}
              >
                {getRiderBookingLabel(booking, t('booking'))}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-3">
        {connectionNoticeKey && (
          <NoticeStrip message={t(connectionNoticeKey)} cn={cn} />
        )}
        {staleNoticeKey && (
          <NoticeStrip message={t(staleNoticeKey)} cn={cn} />
        )}

        <div>
          <div className={cn('text-xs font-semibold uppercase tracking-wide', text?.secondary)}>
            {t(getPhaseKey(summaryModel.phase))}
          </div>
          <div className={cn('mt-1 text-xl font-semibold leading-tight', text?.primary)}>
            {t(summaryModel.primaryKey, formatTranslationValues(summaryModel.primaryValues))}
          </div>
          {summaryModel.secondaryKey && (
            <div className={cn('mt-2 text-sm', text?.secondary)}>
              {t(summaryModel.secondaryKey, formatTranslationValues(summaryModel.secondaryValues))}
            </div>
          )}
        </div>

        {delayNotice && (
          <NoticeStrip
            message={t(delayNotice.messageKey, delayNotice.messageValues)}
            cn={cn}
          />
        )}
        {summaryModel.rideDelayNotice && (
          <NoticeStrip
            message={t(
              summaryModel.rideDelayNotice.messageKey,
              summaryModel.rideDelayNotice.messageValues
            )}
            cn={cn}
          />
        )}

        {summaryModel.metrics.length > 0 && (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {summaryModel.metrics.map((metric) => {
              const companionValue = formatMetricCompanionValue(metric, {
                formatTime,
                formatDateTime,
                t,
                entityTimeZone
              });

              return (
                <div
                  key={metric.labelKey}
                  className={cn(
                    'min-w-0 rounded-md border px-3 py-2',
                    background.card,
                    border?.default
                  )}
                >
                  <div className={cn('truncate text-xs font-medium', text?.secondary)}>
                    {t(metric.labelKey)}
                  </div>
                  <div className={cn('mt-1 text-base font-semibold leading-tight', text?.primary)}>
                    {formatMetricValue(metric, {
                      distanceUnit,
                      formatTime,
                      formatDateTime,
                      t,
                      entityTimeZone
                    })}
                  </div>
                  {companionValue && (
                    <div className={cn('mt-0.5 text-xs leading-tight', text?.secondary)}>
                      {companionValue}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="flex items-start gap-2">
            <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
            <div className="min-w-0">
              <div className={cn('text-xs font-medium', text?.secondary)}>{t('riderTrip.pickup')}</div>
              <div className={cn('break-words text-sm font-medium', text?.primary)}>{pickupName}</div>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-600" />
            <div className="min-w-0">
              <div className={cn('text-xs font-medium', text?.secondary)}>{t('riderTrip.dropoff')}</div>
              <div className={cn('break-words text-sm font-medium', text?.primary)}>{dropoffName}</div>
            </div>
          </div>
        </div>

        <div className={cn('flex flex-wrap gap-3 text-sm', text?.secondary)}>
          <span className="inline-flex items-center gap-1">
            <Users className="h-4 w-4" />
            {selectedBooking.riders} {t('riders')}
          </span>
          <span className="inline-flex items-center gap-1">
            <Briefcase className="h-4 w-4" />
            {selectedBooking.luggages} {t('luggage')}
          </span>
        </div>
      </div>
    </section>
  );
};

export default RiderTripSummary;
